import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { usePreferencesStore } from '@/stores/preferences';

/**
 * Cookie consent, same contract as the Vue 2 portal so users who already answered
 * are not asked again after the upgrade:
 *  - cookie `cookie-consent`, value `analytics=true|false`, `path=/`, expires after 182 days
 *  - the cookie (not localStorage) is the "banner already answered" flag; when it is
 *    missing or expired the banner is shown again and analytics consent is reset to false
 *  - the boolean itself is mirrored into the preferences store (used by telemetry).
 */
export const CONSENT_COOKIE_NAME = 'cookie-consent';
export const CONSENT_COOKIE_EXPIRATION_DAYS = 182;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Raw cookie value (e.g. `analytics=true`), or undefined when absent or ambiguous (duplicated). */
export function getCookieByName(name: string, cookieString: string = document.cookie): string | undefined {
  const parts = `; ${cookieString}`.split(`; ${name}=`);
  if (parts.length !== 2) return undefined;
  return parts.pop()?.split(';').shift();
}

/** `null` = the user has not answered yet; otherwise the analytics choice. */
export function readConsentCookie(cookieString: string = document.cookie): boolean | null {
  const value = getCookieByName(CONSENT_COOKIE_NAME, cookieString);
  if (!value) return null;
  return value.split('=')[1] === 'true';
}

export function writeConsentCookie(analytics: boolean, now: Date = new Date()): void {
  const expires = new Date(now.getTime() + CONSENT_COOKIE_EXPIRATION_DAYS * DAY_MS);
  document.cookie = `${CONSENT_COOKIE_NAME}=analytics=${analytics}; expires=${expires.toUTCString()}; path=/`;
}

// Module-level so every component using the composable shares the same "answered" state.
const answered = ref(false);

export interface CookieConsentApi {
  /** True once the user accepted/rejected/saved (consent cookie present). */
  answered: Readonly<Ref<boolean>>;
  /** Current analytics consent (preferences store). */
  analyticsConsent: ComputedRef<boolean>;
  /** Reads the cookie and syncs the store. Returns whether the user already answered. */
  init: () => boolean;
  /** Persists the choice (cookie + store). */
  save: (analytics: boolean) => void;
}

export function useCookieConsent(): CookieConsentApi {
  const preferences = usePreferencesStore();

  function init(): boolean {
    const stored = readConsentCookie();
    if (stored === null) {
      answered.value = false;
      preferences.setAnalyticsConsent(false);
    } else {
      answered.value = true;
      preferences.setAnalyticsConsent(stored);
    }
    return answered.value;
  }

  function save(analytics: boolean): void {
    writeConsentCookie(analytics);
    answered.value = true;
    preferences.setAnalyticsConsent(analytics);
  }

  return {
    answered,
    analyticsConsent: computed(() => preferences.analyticsConsent),
    init,
    save,
  };
}
